import { z } from 'zod';

// ============================================================================
// Attraction Pricing (P3-ATT-001)
// ============================================================================

const HH_MM = /^\d{2}:\d{2}$/;

export const CreateAttractionPricingRuleRequestSchema = z.object({
  attractionId: z.string().min(1, 'attractionId is required'),
  attractionName: z.string().min(1, 'attractionName is required'),
  baseCreditCost: z.number().int().min(1),
  peakMultiplier: z.number().min(1.0).max(5.0).default(2.0),
  offPeakMultiplier: z.number().min(0.1).max(1.0).default(0.7),
  highQueueThreshold: z.number().int().min(1).default(50),
  lowQueueThreshold: z.number().int().min(0).default(10),
  peakHoursStart: z
    .string()
    .regex(HH_MM, 'peakHoursStart must be in HH:mm format')
    .default('10:00'),
  peakHoursEnd: z
    .string()
    .regex(HH_MM, 'peakHoursEnd must be in HH:mm format')
    .default('16:00'),
  weatherSensitive: z.boolean().default(false),
  isActive: z.boolean().default(true),
});

export type CreateAttractionPricingRuleRequest = z.infer<typeof CreateAttractionPricingRuleRequestSchema>;

export const UpdateAttractionPricingRuleRequestSchema = z.object({
  attractionName: z.string().min(1).optional(),
  baseCreditCost: z.number().int().min(1).optional(),
  peakMultiplier: z.number().min(1.0).max(5.0).optional(),
  offPeakMultiplier: z.number().min(0.1).max(1.0).optional(),
  highQueueThreshold: z.number().int().min(1).optional(),
  lowQueueThreshold: z.number().int().min(0).optional(),
  peakHoursStart: z
    .string()
    .regex(HH_MM, 'peakHoursStart must be in HH:mm format')
    .optional(),
  peakHoursEnd: z
    .string()
    .regex(HH_MM, 'peakHoursEnd must be in HH:mm format')
    .optional(),
  weatherSensitive: z.boolean().optional(),
  isActive: z.boolean().optional(),
});

export type UpdateAttractionPricingRuleRequest = z.infer<typeof UpdateAttractionPricingRuleRequestSchema>;

export const LockAttractionPriceRequestSchema = z.object({
  customerId: z.string().min(1, 'customerId is required'),
});

export type LockAttractionPriceRequest = z.infer<typeof LockAttractionPriceRequestSchema>;

// ============================================================================
// Response schemas — attraction pricing engine outputs
// ============================================================================

// ============================================================================
// Attraction pricing rule record — Prisma AttractionPricingRule as JSON
// Returned by admin CRUD endpoints (GET/POST/PATCH/DELETE /admin/pricing/attractions)
// ============================================================================

export const AttractionPricingRuleRecordSchema = z.object({
  id: z.string(),
  attractionId: z.string(),
  attractionName: z.string(),
  baseCreditCost: z.number().int().positive(),
  peakMultiplier: z.number(),
  offPeakMultiplier: z.number(),
  highQueueThreshold: z.number().int().nonnegative(),
  lowQueueThreshold: z.number().int().nonnegative(),
  peakHoursStart: z.string(),
  peakHoursEnd: z.string(),
  weatherSensitive: z.boolean(),
  isActive: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type AttractionPricingRuleRecord = z.infer<typeof AttractionPricingRuleRecordSchema>;

// Returned by GET /pricing/attractions, GET /pricing/attractions/:id,
// and POST /pricing/attractions/:id/lock
export const AttractionPriceResultSchema = z.object({
  attractionId: z.string(),
  attractionName: z.string(),
  baseCreditCost: z.number().int().positive(),
  currentCreditCost: z.number().int().positive(),
  multiplier: z.number(),
  reasons: z.array(z.string()),
  priceLockKey: z.string().optional(),
  lockExpiresAt: z.string().optional(),
  queueDepth: z.number().int().optional(),
  weatherCondition: z.string().optional(),
});

export type AttractionPriceResult = z.infer<typeof AttractionPriceResultSchema>;
